import { useBiApiClientProvider } from "../../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../../contexts/ApiClientProviderContext.ts";
import { getListExportWarning } from "../../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../../common/export/ExcelReportExport.tsx";
import { Box, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors.ts";
import { InvalidConfigurationReason } from "../../../../utilities/isConfigurationValid.ts";
import PartContainer from "../../../viewer/common/PartContainer.tsx";
import PreviewComponentAlert from "../../../viewer/common/PreviewComponentAlert.tsx";
import PreviewActions from "../../../viewer/common/PreviewActions.tsx";
import EditingHistoryControls from "../../common/EditingHistoryControls.tsx";
import RecordsCounter from "../../common/drillDown/RecordsCounter.tsx";
import ListGrid from "./ListGrid.tsx";
import useListPreviewGrid from "./hooks/useListPreviewGrid.ts";

const ListPreviewGrid = () => {
  const dimensions = useSelector(selectDimensions);
  const {
    validity,
    resultId,
    error,
    failedMeasures,
    dataSource,
    totalRows,
    loading,
    columns,
    sortModel,
    aggregationModel,
    pinnedColumns,
    onRefresh,
    onSortingChanged,
    exportRequestConfig,
  } = useListPreviewGrid();
  const { requestBuildListExport } = useBiApiClientProvider<BiApiClient>();

  return (
    <>
      <ExcelReportExport
        mode="build"
        requestConfig={exportRequestConfig}
        dimensions={dimensions}
        requestExport={requestBuildListExport}
        warning={getListExportWarning(loading, totalRows, validity)}
      />
      <PartContainer
        sx={{ borderRight: "unset", p: "1.3rem", pt: 2 }}
        containerSx={{ position: "relative", gap: "1rem" }}
        caption="Preview"
        actions={<PreviewActions onRefresh={onRefresh} EditingHistory={<EditingHistoryControls />} />}
      >
        {validity.valid ? (
          <Stack sx={{ flex: 1, minHeight: 0, gap: 1 }}>
            <PreviewComponentAlert error={undefined} failedMeasures={failedMeasures} />
            <ListGrid
              loading={loading}
              columns={columns}
              resultId={resultId}
              error={error}
              dataSource={dataSource}
              sortModel={sortModel}
              aggregationModel={aggregationModel}
              pinnedColumns={pinnedColumns}
              onSortingChanged={onSortingChanged}
            />
            <Box>
              <RecordsCounter totalCount={totalRows ?? 0} align="left" />
            </Box>
          </Stack>
        ) : (
          <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <Typography color="textSecondary">
              {validity.reason === InvalidConfigurationReason.MandatoryFieldIsNotSelected
                ? "Set all mandatory parameters to preview the report."
                : "Add at least one dimension field to preview the report."}
            </Typography>
          </Stack>
        )}
      </PartContainer>
    </>
  );
};

export default ListPreviewGrid;
